import { useQuery } from '@tanstack/react-query'
import { useMsal } from '@azure/msal-react'
import { useEffect } from 'react'
import { api } from '../lib/api'
import { applyKnownAuthStatus, resolveBlockedInfo } from '../lib/authErrorMessages'
import { getImpersonationSession } from '../lib/impersonation'
import { useAuthStore, type AuthUser } from '../stores/auth'

export function useCurrentUser() {
  const { accounts } = useMsal()
  const { setUser, setBlocked, setLoading, setPending, setNoInvite, setDenied, setExternal, setNotProvisioned } = useAuthStore()
  // An impersonation tab never has a real MSAL account (see
  // lib/impersonation.ts) -- without this OR, this query would stay
  // permanently disabled and authStatus would stay 'loading' forever for
  // every impersonated session. Checked once per render, same as
  // `accounts.length > 0` itself; a session ending mid-render is handled by
  // api.ts's own 401 branch, not by this flag re-evaluating live.
  const isAuthenticated = accounts.length > 0 || getImpersonationSession() !== null

  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get<{ data: AuthUser }>('/auth/me'),
    enabled: isAuthenticated,
    retry: false,
  })

  useEffect(() => {
    if (query.data) {
      setUser(query.data.data)
    } else if (query.isError) {
      // applyKnownAuthStatus covers every rejection reason this app has a
      // dedicated status/page for. Anything else it doesn't recognize --
      // an error code this hook has never heard of, no code at all, or a
      // raw network failure -- still needs a hard stop via setBlocked()
      // here specifically: this is the one query whose whole job is
      // establishing whether this session belongs here at all, so "we
      // don't know what's going on" must fail closed, never fall through
      // to a state App.tsx's route guard would treat as safe. This is why
      // this effect keeps its own fallback rather than relying solely on
      // the global QueryCache hook (main.tsx), which deliberately does NOT
      // apply that same fallback to other queries. See quill-bug-log.md,
      // "Suspending a tenant mid-session..." for the original incident this
      // replaced (`else clearUser()`, which let a real 403 reach AppShell).
      const handled = applyKnownAuthStatus(query.error, {
        setPending, setNoInvite, setDenied, setExternal, setNotProvisioned, setBlocked,
      })
      if (!handled) setBlocked(resolveBlockedInfo(query.error))
    } else {
      setLoading(query.isLoading)
    }
  }, [query.data, query.isError, query.error, query.isLoading, setUser, setBlocked, setLoading, setPending, setNoInvite, setDenied, setExternal, setNotProvisioned])

  return query
}
